import React, { useCallback, useContext, useEffect, useState } from 'react'
import FormBuilderUi from '@telus/form-builder-ui'
import ErrorDisplay from '@/siteBuilder/components/errors/ErrorDisplay'
import { Spinner } from '@telus-uds/components-web'
import { isEmptyObj } from '@/siteBuilder/utils/isEmptyObj'

import { SpacerProps } from '../Spacer/Spacer'
import Row from '../Row/Row'
import StyledWrapper, { DivWrapper } from './styles'
import getConfig from 'next/config'
import { BlockRenderer } from '../../../renderer/contentful/BlockRenderer'
import { useHookWithRefCallback } from '../../../../hooks/useHookWithRefCallback'
import { hashEmail, isRow } from './FormBuilder.utils'
import { renderSpacer } from 'src/siteBuilder/utils/renderSpacer'
import callApi from './FormBuilderUtils/callApi'
import { SiteBuilderContext } from '../../../renderer/context/SiteBuilderContext'
import useLocalStorage from 'src/siteBuilder/utils/useLocalStorage'

const THEME_MAP = {
  koodo: 'Koodo',
  telus: 'Allium',
}
const {
  publicRuntimeConfig: { API_BASE_PATH, IS_PROD },
} = getConfig()

const EMAIL_FIELD_CONTENT_TYPE = 'emailField'
const ACCESS_CONFIG = 'sb2'

export type FormBuilderProps = {
  form: any
  sys: any
  heading?: any
  successfulPostSubmitMessage?: any
  unsuccessfulPostSubmitMessage?: any
  spacer?: SpacerProps

  apiPath?: string
  constructDataObject?: Record<string, any>
  errorDisplay?: React.ReactElement
  customErrorPage?: boolean
  extraData?: object
  submitFormHandler?: () => void
}

export interface IConstructDataObject {
  keyPathArray?: IConstructData[]
}

export interface IConstructData {
  key: string
  path: (string | number)[]
}

export interface ICMP {
  campaignCode?: string
  expires?: string
}

const FormBuilder = ({
  form,
  sys = { locale: '' },
  heading,
  successfulPostSubmitMessage,
  unsuccessfulPostSubmitMessage,
  spacer,
  apiPath = '',
  constructDataObject = {},
  errorDisplay = undefined,
  customErrorPage = false,
  extraData,
  submitFormHandler,
}: FormBuilderProps) => {
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [backendSucceeded, setBackendSucceeded] = useState(false)
  const [scrollRef] = useHookWithRefCallback()

  const [prefillValues, setPrefillValues] = useState({})
  const [hasApiError, setHasApiError] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const { brand } = useContext(SiteBuilderContext)

  const [campaignCode] = useLocalStorage<ICMP>('cmp', {})

  const cmp = campaignCode?.campaignCode ? { cmp: campaignCode.campaignCode } : {}

  const fetchPrefillValues = useCallback(async () => {
    if (apiPath && !isEmptyObj(constructDataObject)) {
      try {
        const response = await callApi(`${API_BASE_PATH}${apiPath}`, constructDataObject, setHasApiError)
        if (response) {
          setIsLoading(false)
          if (response) {
            setPrefillValues(response)
          } else {
            setHasApiError(true)
          }
        }
      } catch (e) {
        console.error('api error', e)
        setHasApiError(true)
      }
    } else {
      setIsLoading(false)
    }
  }, [apiPath, constructDataObject])

  useEffect(() => {
    void fetchPrefillValues()
  }, [])

  const hasForm = !!form && typeof form === 'object' && 'sys' in form
  if (!hasForm) {
    // The form is required beyond this point, so exit if it's missing
    return null
  }

  const hasCustomPostSubmitMessage = isRow(successfulPostSubmitMessage)
  const hasUnsuccessfulPostSubmitMessage = isRow(unsuccessfulPostSubmitMessage)

  const formSpaceID = form.sys.space.sys.id
  // As long as the contentful environment is not main, we can disable caching
  // So devs/content writers can quickly see changes applied
  const prodEnvironment = 'production'
  const onSubmit = ({
    submitButtonLabel,
    entryTitle: formName,
    fields,
    isFormValid,
    backendSuccessful,
    consentField,
  }) => {
    // This determines if the confirmation message will be displayed.
    setHasSubmitted(isFormValid as boolean)
    setBackendSucceeded(backendSuccessful as boolean)

    // Performs additional action on form submission when passed in as a prop. Currently passed in from FormWithUrlParams for the purpose of performing a state change to hide dynamic content on submission.
    if (submitFormHandler) {
      submitFormHandler()
    }
  }

  const customSuccess = () => {
    // After form has been submitted, if backend has no error and we have custom message, show it
    // same logic applies if backend has error. But if a) there is no custom message, or b) form
    // has yet to be submitted, render the FormBuilder component to a) leverage its built in post
    // submitssion message, or b) render the actual form
    if (backendSucceeded) {
      if (hasCustomPostSubmitMessage) {
        return (
          <DivWrapper ref={scrollRef} data-testid="scroll-ref-div">
            <Row
              {...successfulPostSubmitMessage.fields}
              key={successfulPostSubmitMessage.sys.id}
              id={successfulPostSubmitMessage.fields.sectionId}
              rowBackgroundColor={successfulPostSubmitMessage.fields.background?.fields.color}
              rowBackgroundWidth={successfulPostSubmitMessage.fields.background?.fields.width}
            >
              <BlockRenderer block={successfulPostSubmitMessage.fields.children} />
            </Row>
          </DivWrapper>
        )
      }
    } else if (hasUnsuccessfulPostSubmitMessage) {
      return (
        <DivWrapper ref={scrollRef} data-testid="scroll-ref-div">
          <Row
            {...unsuccessfulPostSubmitMessage.fields}
            key={unsuccessfulPostSubmitMessage.sys.id}
            id={unsuccessfulPostSubmitMessage.fields.sectionId}
            rowBackgroundColor={unsuccessfulPostSubmitMessage.fields.background?.fields.color}
            rowBackgroundWidth={unsuccessfulPostSubmitMessage.fields.background?.fields.width}
          >
            <BlockRenderer block={unsuccessfulPostSubmitMessage.fields.children} />
          </Row>
        </DivWrapper>
      )
    }
  }

  const renderForm = () => {
    return (
      <React.Fragment>
        {!hasSubmitted && isRow(heading) && (
          <Row
            {...heading.fields}
            key={heading.sys.id}
            id={heading.fields.sectionId}
            rowBackgroundColor={heading.fields.background?.fields.color}
            rowBackgroundWidth={heading.fields.background?.fields.width}
          >
            <BlockRenderer block={heading.fields.children} />
          </Row>
        )}
        {hasSubmitted && customSuccess()}
        <FormBuilderUi
          locale={sys.locale}
          entryID={form.sys.id}
          spaceID={formSpaceID}
          environment={form.sys.environment.sys.id}
          usePreviewAPI={!IS_PROD}
          bypassCache={true}
          onSubmit={onSubmit}
          isProduction={IS_PROD}
          hasCustomPostSubmitMessage={hasCustomPostSubmitMessage}
          hasUnsuccessfulPostSubmitMessage={hasUnsuccessfulPostSubmitMessage}
          accessConfig={ACCESS_CONFIG}
          preFillValues={{ ...prefillValues, ...extraData, ...cmp }}
          theme={THEME_MAP[brand]}
          validationApiConfig={form?.preSubmitApiCall}
        />
      </React.Fragment>
    )
  }

  const errorPage = () => {
    return customErrorPage ? <>{errorDisplay}</> : <ErrorDisplay />
  }

  // if API throws error, the form should not be displayed
  // form may have required hidden values that is required
  if (hasApiError) {
    return errorPage()
  }

  return (
    <StyledWrapper>
      {renderSpacer(spacer)}

      <Spinner inline={true} show={isLoading} label="Loading">
        {renderForm()}
      </Spinner>
    </StyledWrapper>
  )
}

export default FormBuilder
